/**
 * Stone Investment Property Map View
 * 
 * Map view for properties with Stone Investment's luxury aesthetic:
 * - Split view (map + list)
 * - Property markers with popups
 * - Interactive filtering
 */

'use client';

import { FormatPrice } from '@/components/ui/FormatPrice';
import { useState } from 'react';
import { MapPin, List, Grid3X3, ChevronLeft, ChevronRight } from 'lucide-react';
import { StoneListingCard } from './StoneListingCard';
import type { ListingCardData } from '@/components/listings/types';

export interface StonePropertyMapViewProps {
  /**
   * Properties to display
   */
  properties: ListingCardData[];
  /**
   * Base path for property links
   */
  basePath: string;
  /**
   * Initial map center
   */
  center?: {
    latitude: number;
    longitude: number;
  };
  /**
   * Initial zoom level
   */
  zoom?: number;
  /**
   * Current locale
   */
  locale?: string;
  /**
   * Translation messages
   */
  messages?: Record<string, string>;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StonePropertyMapView - Map view for properties
 */
export function StonePropertyMapView({
  properties,
  basePath,
  center,
  zoom: _zoom = 12,
  locale = 'en',
  messages = {},
  className = '',
}: StonePropertyMapViewProps) {
  const [selectedProperty, setSelectedProperty] = useState<ListingCardData | null>(null);
  const [showList, setShowList] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const propertiesPerPage = 4;
  
  // Calculate map center from properties if not provided
  const mapCenter = center || (properties.length > 0 ? {
    latitude: properties.reduce((sum, p) => sum + (p.location?.latitude || 0), 0) / properties.length,
    longitude: properties.reduce((sum, p) => sum + (p.location?.longitude || 0), 0) / properties.length,
  } : { latitude: 43.6047, longitude: 1.4442 }); // Default to Toulouse
  
  // Paginate properties
  const totalPages = Math.ceil(properties.length / propertiesPerPage);
  const paginatedProperties = properties.slice(
    (currentPage - 1) * propertiesPerPage,
    currentPage * propertiesPerPage
  );
  
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapCenter.latitude},${mapCenter.longitude}`;
  
  return (
    <div className={`flex flex-col lg:flex-row min-h-[600px] ${className}`}>
      {/* Map Section */}
      <div className={`relative bg-stone-200 ${showList ? 'lg:w-1/2' : 'w-full'} min-h-[400px] lg:min-h-full`}>
        {/* Map Placeholder Pattern */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='%23d6d3d1' fill-opacity='0.3'/%3E%3C/svg%3E")`,
            backgroundSize: '30px 30px',
          }}
        />
        
        {/* Property Markers Visualization */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-64 h-64">
            {properties.slice(0, 8).map((property, index) => {
              const angle = (index / Math.min(8, properties.length)) * 2 * Math.PI;
              const radius = 80 + (index % 2) * 20;
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;
              
              return (
                <button
                  key={property.id}
                  onClick={() => setSelectedProperty(property)}
                  className={`absolute w-8 h-8 flex items-center justify-center transition-all duration-300 ${
                    selectedProperty?.id === property.id 
                      ? 'bg-stone-900 scale-125 z-10' 
                      : 'bg-stone-700 hover:bg-stone-800'
                  }`}
                  style={{
                    left: `calc(50% + ${x}px - 16px)`,
                    top: `calc(50% + ${y}px - 16px)`,
                  }}
                >
                  <MapPin size={16} className="text-white" strokeWidth={1.5} />
                </button>
              );
            })}
            
            {/* Center Marker */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-stone-400 rounded-full" />
          </div>
        </div>
        
        {/* View Toggle */}
        <div className="absolute top-4 left-4 flex gap-2 z-10">
          <button
            onClick={() => setShowList(true)}
            className={`px-3 py-2 text-sm font-sans flex items-center gap-2 transition-colors ${
              showList 
                ? 'bg-stone-900 text-white' 
                : 'bg-white/90 text-stone-700 hover:bg-white'
            }`}
          >
            <List size={16} />
            <span className="hidden sm:inline">List</span>
          </button>
          <button
            onClick={() => setShowList(false)}
            className={`px-3 py-2 text-sm font-sans flex items-center gap-2 transition-colors ${
              !showList 
                ? 'bg-stone-900 text-white' 
                : 'bg-white/90 text-stone-700 hover:bg-white'
            }`}
          >
            <Grid3X3 size={16} />
            <span className="hidden sm:inline">Map Only</span>
          </button>
        </div>
        
        {/* Open in Google Maps */}
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-4 right-4 px-4 py-2 bg-white/90 text-sm font-sans text-stone-700 hover:text-stone-900 transition-colors z-10"
        >
          Open in Maps
        </a>
        
        {/* Selected Property Popup */}
        {selectedProperty && (
          <div className="absolute bottom-4 left-4 right-4 bg-white p-4 shadow-lg z-10">
            <div className="flex gap-4">
              {selectedProperty.property.primaryImageUrl && (
                <div 
                  className="w-24 h-24 bg-stone-200 bg-cover bg-center flex-shrink-0"
                  style={{ backgroundImage: `url(${selectedProperty.property.primaryImageUrl})` }}
                />
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-serif text-lg text-stone-900 truncate">
                  {selectedProperty.property.name}
                </h4>
                <p className="text-sm text-stone-500 font-sans truncate">
                  {[
                    selectedProperty.location?.suburb,
                    selectedProperty.location?.city,
                    selectedProperty.location?.district,
                    selectedProperty.location?.neighbourhood,
                  ].filter(Boolean).join(', ') || selectedProperty.location?.name || ''}
                </p>
                {selectedProperty.price && (
                  <FormatPrice
                    amount={selectedProperty.price.amount}
                    currency={selectedProperty.price.currency}
                    className="font-serif text-xl text-stone-900 mt-2"
                    size="lg"
                  />
                )}
              </div>
              <button
                onClick={() => setSelectedProperty(null)}
                className="text-stone-400 hover:text-stone-600 self-start"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        {/* Properties Count */}
        <div className="absolute bottom-4 right-4 px-3 py-2 bg-stone-900 text-white text-xs font-sans z-10">
          {properties.length} {properties.length === 1 ? 'property' : 'properties'}
        </div>
      </div>
      
      {/* List Section */}
      {showList && (
        <div className="lg:w-1/2 bg-stone-50 overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-serif text-xl text-stone-900">
                {messages.properties || 'Properties'}
              </h3>
              <p className="text-sm text-stone-500 font-sans">
                {properties.length} {messages.results || 'results'}
              </p>
            </div>
            
            {/* Property List */}
            <div className="space-y-6">
              {paginatedProperties.map((property) => (
                <StoneListingCard
                  key={property.id}
                  listing={property}
                  href={`${basePath}/properties/${property.property.id}`}
                  variant="landscape"
                  showAgent={false}
                  showStatus={true}
                  locale={locale}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8 pt-6 border-t border-stone-200">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-10 h-10 flex items-center justify-center border border-stone-300 text-stone-600 hover:border-stone-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                
                <span className="text-sm font-sans text-stone-600">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-10 h-10 flex items-center justify-center border border-stone-300 text-stone-600 hover:border-stone-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StonePropertyMapView;
